import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Check, Shield } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useRemoteSession } from '../contexts/RemoteSessionContext';
import type { RemoteSession } from '../services/superAdminService';

type RRWebEvent = Record<string, unknown>;

const RRWEB_CDN = 'https://cdn.jsdelivr.net/npm/rrweb@2.0.0-alpha.13/dist/rrweb.min.js';
const RRWEB_LOAD_TIMEOUT_MS = 15_000;

/** Load rrweb at runtime. Retries once on failure. No crossOrigin attr to avoid
 *  CORS issues in Capacitor WKWebView (origin is capacitor://localhost). */
function loadRRWebOnce(): Promise<{ record: (opts: Record<string, unknown>) => () => void }> {
  return new Promise((resolve, reject) => {
    const w = window as Window & { __rrweb?: { record: (opts: Record<string, unknown>) => () => void } };
    if (w.__rrweb?.record) {
      resolve(w.__rrweb);
      return;
    }

    const timeout = setTimeout(() => {
      script.onload = null;
      script.onerror = null;
      script.remove();
      reject(new Error('rrweb script load timed out'));
    }, RRWEB_LOAD_TIMEOUT_MS);

    const script = document.createElement('script');
    script.src = RRWEB_CDN;
    script.onload = () => {
      clearTimeout(timeout);
      const mod = w.__rrweb ?? (w as unknown as Record<string, { record: (opts: Record<string, unknown>) => () => void }>).rrweb;
      if (mod?.record) {
        w.__rrweb = mod;
        resolve(mod);
      } else {
        reject(new Error('rrweb did not expose record'));
      }
    };
    script.onerror = () => {
      clearTimeout(timeout);
      script.remove();
      reject(new Error('Failed to load rrweb script'));
    };
    document.head.appendChild(script);
  });
}

async function loadRRWeb(): Promise<{ record: (opts: Record<string, unknown>) => () => void }> {
  try {
    return await loadRRWebOnce();
  } catch (firstErr) {
    console.warn('rrweb first load attempt failed, retrying…', firstErr);
    await new Promise(r => setTimeout(r, 1500));
    return loadRRWebOnce();
  }
}

export function RemoteSessionListener({ userId }: { userId: string }) {
  const { setIsBeingMonitored, registerEndSession } = useRemoteSession();
  const [pendingSession, setPendingSession] = useState<RemoteSession | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const activeSessionIdRef = useRef<string | null>(null);
  const stopRecordingRef = useRef<(() => void) | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const batchBufferRef = useRef<RRWebEvent[]>([]);
  const flushIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const leadingFlushRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushEvents = useCallback(() => {
    if (batchBufferRef.current.length === 0 || !channelRef.current || !activeSessionIdRef.current) return;

    const events = batchBufferRef.current;
    batchBufferRef.current = [];

    channelRef.current.send({
      type: 'broadcast',
      event: 'rrweb_events',
      payload: { events },
    });
  }, []);

  const pushEvent = useCallback((event: RRWebEvent) => {
    const wasEmpty = batchBufferRef.current.length === 0;
    batchBufferRef.current.push(event);
    // Leading-edge: flush quickly after the first event in an idle batch
    if (wasEmpty && !leadingFlushRef.current) {
      leadingFlushRef.current = setTimeout(() => {
        leadingFlushRef.current = null;
        flushEvents();
      }, 30);
    }
  }, [flushEvents]);

  const startRecording = useCallback(async (sessionId: string) => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    try {
      activeSessionIdRef.current = sessionId;

      channel = supabase.channel(`remote-session:${sessionId}`, {
        config: { broadcast: { self: false } },
      });

      channel.on('broadcast', { event: 'session_status' }, (payload) => {
        if (payload.payload?.status === 'ended') {
          stopSession('admin');
        }
      });

      await new Promise<void>((resolve, reject) => {
        channel!.subscribe((subscribedStatus) => {
          if (subscribedStatus === 'SUBSCRIBED') {
            resolve();
          } else if (subscribedStatus === 'CHANNEL_ERROR' || subscribedStatus === 'TIMED_OUT') {
            console.warn('[RemoteListener] Broadcast channel error during recording, rebuilding…');
            try { channel!.unsubscribe(); } catch { /* ignore */ }
            const rebuilt = supabase.channel(`remote-session:${sessionId}`, {
              config: { broadcast: { self: false } },
            });
            rebuilt.on('broadcast', { event: 'session_status' }, (payload) => {
              if (payload.payload?.status === 'ended') {
                stopSession('admin');
              }
            });
            rebuilt.subscribe();
            channelRef.current = rebuilt;
          }
        });
      });
      channelRef.current = channel;

      const rrweb = await loadRRWeb();
      const stop = rrweb.record({
        emit(event, isCheckout) {
          pushEvent(event as unknown as RRWebEvent);
          if (isCheckout) flushEvents();
        },
        maskAllInputs: true,
        blockClass: 'remote-session-block',
        inlineStylesheet: true,
        checkoutEveryNms: 12000,
        sampling: {
          mousemove: false,
          mouseInteraction: true,
          scroll: 80,
          input: 'last',
        },
      });

      stopRecordingRef.current = stop;

      flushIntervalRef.current = setInterval(flushEvents, 80);

      await supabase
        .from('remote_sessions')
        .update({ status: 'active' })
        .eq('id', sessionId);

      channel.send({
        type: 'broadcast',
        event: 'session_status',
        payload: { status: 'active' },
      });

      setIsRecording(true);
      setActiveSessionId(sessionId);
      setIsBeingMonitored(true);
      registerEndSession(() => stopSession('user'));
    } catch (error) {
      console.error('Failed to start rrweb recording:', error);

      // Let the admin know recording failed so they don't wait forever
      if (channel) {
        try {
          channel.send({
            type: 'broadcast',
            event: 'session_status',
            payload: { status: 'error', message: 'Recording failed to start on user device' },
          });
        } catch { /* best effort */ }
        channel.unsubscribe();
        channelRef.current = null;
      }

      await supabase
        .from('remote_sessions')
        .update({ status: 'ended', ended_at: new Date().toISOString(), ended_by: 'user' })
        .eq('id', sessionId);

      activeSessionIdRef.current = null;
      setPendingSession(null);
    }
  }, [flushEvents, pushEvent, setIsBeingMonitored, registerEndSession]);

  const stopSession = useCallback(async (endedBy: 'user' | 'admin' | 'app_closed' = 'user') => {
    if (leadingFlushRef.current) {
      clearTimeout(leadingFlushRef.current);
      leadingFlushRef.current = null;
    }
    if (flushIntervalRef.current) {
      clearInterval(flushIntervalRef.current);
      flushIntervalRef.current = null;
    }

    flushEvents();

    if (stopRecordingRef.current) {
      stopRecordingRef.current();
      stopRecordingRef.current = null;
    }

    if (channelRef.current) {
      channelRef.current.send({
        type: 'broadcast',
        event: 'session_status',
        payload: { status: 'ended' },
      });
      channelRef.current.unsubscribe();
      channelRef.current = null;
    }

    const sessionId = activeSessionIdRef.current;
    if (sessionId) {
      await supabase
        .from('remote_sessions')
        .update({
          status: 'ended',
          ended_at: new Date().toISOString(),
          ended_by: endedBy,
        })
        .eq('id', sessionId);
    }

    setIsRecording(false);
    setActiveSessionId(null);
    activeSessionIdRef.current = null;
    setPendingSession(null);
    setIsBeingMonitored(false);
    registerEndSession(null);
  }, [flushEvents, setIsBeingMonitored, registerEndSession]);

  const handleApprove = useCallback(async () => {
    if (!pendingSession) return;

    const sessionId = pendingSession.id;

    await supabase
      .from('remote_sessions')
      .update({
        status: 'approved',
        approved_at: new Date().toISOString(),
      })
      .eq('id', sessionId);

    setPendingSession(null);

    // Give React a frame to unmount the approval modal before rrweb takes
    // its initial DOM snapshot. setTimeout(0) is more reliable than
    // requestAnimationFrame inside iOS WKWebView.
    await new Promise(resolve => setTimeout(resolve, 120));

    await startRecording(sessionId);
  }, [pendingSession, startRecording]);

  const handleReject = useCallback(async () => {
    if (!pendingSession) return;

    await supabase
      .from('remote_sessions')
      .update({ status: 'rejected' })
      .eq('id', pendingSession.id);

    setPendingSession(null);
  }, [pendingSession]);

  // Listen for new remote session requests via Postgres changes
  // AND check for any existing pending sessions on mount (in case the INSERT
  // happened before the subscription was established, e.g. user opened app via push notification)
  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    const checkExistingPending = async () => {
      try {
        const { data } = await supabase
          .from('remote_sessions')
          .select('*')
          .eq('target_user_id', userId)
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(1);

        if (!cancelled && data && data.length > 0) {
          setPendingSession(data[0] as RemoteSession);
        }
      } catch (err) {
        console.error('Error checking existing pending sessions:', err);
      }
    };

    const createSubscription = () => {
      return supabase
        .channel(`remote-session-requests:${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'remote_sessions',
            filter: `target_user_id=eq.${userId}`,
          },
          (payload) => {
            const session = payload.new as RemoteSession;
            if (session.status === 'pending') {
              setPendingSession(session);
            }
          }
        )
        .subscribe();
    };

    checkExistingPending();
    let channel = createSubscription();

    // Reconnect subscription and re-check when app returns to foreground (iOS fix)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !cancelled) {
        channel.unsubscribe();
        channel = createSubscription();
        checkExistingPending();
      }
    };

    const handleFocus = () => {
      if (!cancelled) checkExistingPending();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Capacitor appStateChange for native iOS
    let removeAppStateListener: (() => void) | null = null;
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive && !cancelled) {
          channel.unsubscribe();
          channel = createSubscription();
          checkExistingPending();
        }
      }).then(l => { removeAppStateListener = () => l.remove(); });

      // Listen for foreground push notifications containing remote access requests
      import('@capacitor/push-notifications').then(({ PushNotifications }) => {
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          const data = notification.data as Record<string, string> | undefined;
          if (data?.type === 'remote_access_request' && !cancelled) {
            checkExistingPending();
          }
        }).then(l => {
          if (!cancelled) {
            removeAppStateListener = (() => {
              const prev = removeAppStateListener;
              return () => { prev?.(); l.remove(); };
            })();
          } else {
            l.remove();
          }
        });
      }).catch(() => {});
    }

    return () => {
      cancelled = true;
      channel.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      removeAppStateListener?.();
    };
  }, [userId]);

  // Handle app lifecycle (Capacitor) — pause on background, reconnect + resume on foreground
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !isRecording) return;

    const listener = CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
      const sessionId = activeSessionIdRef.current;
      if (!sessionId) return;

      if (!isActive) {
        if (stopRecordingRef.current) {
          stopRecordingRef.current();
          stopRecordingRef.current = null;
        }
        if (flushIntervalRef.current) {
          clearInterval(flushIntervalRef.current);
          flushIntervalRef.current = null;
        }
        flushEvents();

        await supabase
          .from('remote_sessions')
          .update({ status: 'paused' })
          .eq('id', sessionId);

        channelRef.current?.send({
          type: 'broadcast',
          event: 'session_status',
          payload: { status: 'paused' },
        });
      } else {
        try {
          // iOS kills WebSocket connections when backgrounded — rebuild the
          // broadcast channel before resuming recording so events actually reach
          // the admin viewer.
          if (channelRef.current) {
            channelRef.current.unsubscribe();
            channelRef.current = null;
          }

          const newChannel = supabase.channel(`remote-session:${sessionId}`, {
            config: { broadcast: { self: false } },
          });
          newChannel.on('broadcast', { event: 'session_status' }, (payload) => {
            if (payload.payload?.status === 'ended') {
              stopSession('admin');
            }
          });
          await newChannel.subscribe();
          channelRef.current = newChannel;

          const rrweb = await loadRRWeb();
          const stop = rrweb.record({
            emit(event, isCheckout) {
              pushEvent(event as unknown as RRWebEvent);
              if (isCheckout) flushEvents();
            },
            maskAllInputs: true,
            blockClass: 'remote-session-block',
            inlineStylesheet: true,
            checkoutEveryNms: 12000,
            sampling: {
              mousemove: false,
              mouseInteraction: true,
              scroll: 80,
              input: 'last',
            },
          });
          stopRecordingRef.current = stop;
          flushIntervalRef.current = setInterval(flushEvents, 80);

          await supabase
            .from('remote_sessions')
            .update({ status: 'active' })
            .eq('id', sessionId);

          channelRef.current.send({
            type: 'broadcast',
            event: 'session_status',
            payload: { status: 'active' },
          });
        } catch (error) {
          console.error('Failed to resume recording:', error);
        }
      }
    });

    return () => {
      listener.then(l => l.remove());
    };
  }, [isRecording, activeSessionId, flushEvents, pushEvent]);

  // Cleanup on unmount (app close)
  useEffect(() => {
    return () => {
      if (isRecording) {
        stopSession('app_closed');
      }
    };
  }, [isRecording, stopSession]);

  return (
    <>
      {/* Approval Dialog — remote-session-block prevents rrweb from capturing it */}
      {pendingSession && !isRecording && (
        <div className="remote-session-block fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-6 p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="h-14 w-14 rounded-full bg-indigo-100 flex items-center justify-center">
                <Shield className="h-7 w-7 text-indigo-600" />
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 text-center mb-1">
              Remote Access Request
            </h3>
            <p className="text-sm text-gray-500 text-center mb-4">
              A super admin is requesting to view your screen
            </p>

            <div className="bg-gray-50 rounded-xl p-4 mb-4 text-center">
              <p className="text-xs text-gray-500 mb-1">Session Code</p>
              <p className="text-2xl font-mono font-bold tracking-widest text-gray-900">
                {pendingSession.session_code}
              </p>
            </div>

            <p className="text-xs text-gray-400 text-center mb-5">
              Your screen will be view-only. You can end the session at any time.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={handleReject}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
              >
                <X className="h-4 w-4" />
                <span>Deny</span>
              </button>
              <button
                onClick={handleApprove}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors font-medium"
              >
                <Check className="h-4 w-4" />
                <span>Approve</span>
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
